<template>
    <app-layout title="Edit Product">
        <form @submit.prevent="submit">
            <page-header>
                <template #title>Edit Product - {{ product.id }}</template>
                <template #actions>
                    <jet-tertiary-button
                        :disabled="!previous_product"
                        :href="redirectToPreviousProduct"
                    >
                        <font-awesome-icon :icon="['fas', 'chevron-left']" class="mr-2" />
                        Prev
                    </jet-tertiary-button>

                    <jet-tertiary-button 
                        :disabled="!next_product"
                        :href="redirectToNextProduct"
                        class="mr-4"
                    >
                        Next
                        <font-awesome-icon :icon="['fas', 'chevron-right']" class="ml-2" />
                    </jet-tertiary-button>

                    <jet-button v-show="form.isDirty" @click="submit(false)" :disabled="form.processing" >
                        Save
                    </jet-button>

                    <jet-button v-show="form.isDirty" type="submit" :disabled="form.processing" >
                        Save &amp; Close
                    </jet-button>

                    <jet-tertiary-button
                        v-if="form.isDirty"
                        :disabled="form.processing"
                        @click="showUnsavedChangesModal = true"
                    >
                        Cancel
                    </jet-tertiary-button>

                    <jet-tertiary-button
                        v-else
                        :disabled="form.processing"
                        :href="backUrl"
                    >
                        Close
                    </jet-tertiary-button>
                </template>
            </page-header>

            <product-form
                :product="product"
                :categories="categories"
                :productsForContentsSelect="productsForContentsSelect"
                :form="form"
                :master_products="master_products"
                :unit_of_measures="unit_of_measures"
                :location="location"
                :brands="brands"
                :sfd_product_count="sfd_product_count"
                :max_sfd_product_count="max_sfd_product_count"
                :purchasers="purchasers"
                class="px-8"
            />

            <div v-if="!isSubproduct" class="w-full text-center mt-8 mb-4">
                <jet-danger-button @click="showDeleteModal = true">
                    Delete Product
                </jet-danger-button>
            </div>
            <div v-else class="w-full text-center text-red-500 mt-8 mb-4">
                Cannot delete this item, it is part of a gift basket or produce box
            </div>
        </form>

        <unsaved-changes-modal-2
            :formRoute="formRoute"
            :isDirty="form.isDirty"
            :show="showUnsavedChangesModal"
            @close="showUnsavedChangesModal = false"
            @confirm="$inertia.get(backUrl)"
        />

        <jet-confirmation-modal :show="showDeleteModal" @close="showDeleteModal=false">
            <template #title>
                Delete Product
            </template>

            <template #content>
                Are you sure you would like to delete this product?
            </template>

            <template #footer>
                <jet-tertiary-button @click="showDeleteModal=false">
                    Cancel
                </jet-tertiary-button>

                <jet-danger-button class="ml-2" @click="deleteProduct" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                    Delete
                </jet-danger-button>
            </template>
        </jet-confirmation-modal>
    </app-layout>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3';
import JetButton from '@/Jetstream/Button';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import JetDangerButton from '@/Jetstream/DangerButton';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import ProductForm from '@/Pages/Product/Partials/ProductForm.vue';
import UnsavedChangesModal2 from '@/Shared/UnsavedChangesModal2';

export default {
    setup(props) {
        let pricing = [];

        props.pricingLevels.forEach(l => {
            const match = props.product.pricing.find(p => p.pricing_level_id === l.id);

            pricing.push({
                product_id: props.product.id,
                pricing_level_id: l.id,
                pricing_level_title: l.title,
                price: match ? match.price : null,
                modifier_type: match ? match.modifier_type : null,
                modifier_amount: match ? match.modifier_amount : null,
            });
        });

        const form = useForm({
            is_wholesale: props.product.is_wholesale,
            id: props.product.id,
            category_id: props.product.category_id,
            title: props.product.title,
            description: props.product.description,
            sort_by: props.product.sort_by,
            size: props.product.size,
            hot_buy: props.product.hot_buy,
            local: props.product.local,
            special_order: props.product.special_order,
            retail_special: props.product.retail_special,
            allow_oversell: props.product.allow_oversell,
            processed: props.product.processed,
            taxable: props.product.taxable,
            sfd_product: props.product.sfd_product,
            sfd_product_number: props.product.sfd_product_number,
            allow_partial_quantity: props.product.allow_partial_quantity,
            enabled: props.product.enabled,
            image: props.product.image,
            images: props.images,
            cost: props.product.cost,
            price: props.product.price,
            price_2plus: props.product.price_2plus,
            pricing: pricing,
            contents: props.product.contents,
            stock: props.product.stock,
            zone: props.product.zone,
            subzone: props.product.subzone,
            retail_zone: props.product.retail_zone,
            retail_subzone: props.product.retail_subzone,
            school_zone: props.product.school_zone,
            school_subzone: props.product.school_subzone,
            new: props.product.new,
            organic: props.product.organic,
            brand_new: null,
            brand_id: props.product.brand_id,
            gift_card: props.product.gift_card,
            subproducts: props.product.subproducts,
            long_description: props.product.long_description,
            custom_sold_out_message: props.product.custom_sold_out_message,
            require_subitem_quantities: props.product.require_subitem_quantities ? true : false,
            include_subitems_on_picksheet: props.product.include_subitems_on_picksheet ? true : false,
            max_order_qty: props.product.max_order_qty,
            store_excludes: props.product.store_excludes,
            is_master_product: props.product.is_master_product,
            related_master_product_id: props.product.related_master_product_id,
            conversion_factor: props.product.conversion_factor,
            unit_of_measure_id: props.product.unit_of_measure_id,
            picksheet_highlights: props.product.picksheet_highlights,
            purchaser_id: props.product.purchaser_id,
            free_for_orders_over_enabled: props.product.free_for_orders_over_enabled,
            free_for_orders_over_amount: props.product.free_for_orders_over_amount
        })

        return { form }
    },

    components: {
        AppLayout,
        PageHeader,
        JetButton,
        JetConfirmationModal,
        JetDangerButton,
        JetTertiaryButton,
        ProductForm,
        UnsavedChangesModal2,
    },

    props: [
        'location',
        'images',
        'productsForContentsSelect',
        'brands',
        'product',
        'sfd_product_count',
        'max_sfd_product_count',
        'categories',
        'pricingLevels',
        'isSubproduct',
        'master_products',
        'unit_of_measures',
        'next_product',
        'previous_product',
        'purchasers'
    ],

    data() {
        return {
            formRoute: route('products.update.post', {
                product: this.product.id
            }),
            closePage: true,
            showDeleteModal: false,
            showUnsavedChangesModal: false,
        }
    },

    computed: {
        backUrl () {
            const params = route().params;

            if (params.return) {
                return params.return;
            }

            return route('products.index', {
                page: this.$page.props.page.products,
            });
        },
        
        redirectToPreviousProduct() {
            // Redirect to the previous product's edit page
            if (this.previous_product) 
                return route('products.edit', this.previous_product);
        },

        redirectToNextProduct() {
            // Redirect to the previous product's edit page
            if (this.next_product)
                return route('products.edit', this.next_product);
        }
    },

    methods: {
        submit(closePageOnSubmit = true) {
            this.closePage = closePageOnSubmit;

            if(!this.form.processing) {
                this.form.transform((data) => ({
                    ...data,
                    sort_by: data.is_wholesale ? null : data.sort_by,
                    contents: data.is_wholesale ? null : data.contents,
                    retail_zone: data.is_wholesale ? null : data.retail_zone,
                    retail_subzone: data.is_wholesale ? null : data.retail_subzone,
                    new: data.is_wholesale ? 0 : data.new,
                    brand_id: data.is_wholesale ? null : data.brand_id,
                    gift_card: data.is_wholesale ? 0 : data.gift_card,
                    max_order_qty: data.is_wholesale ? null : data.max_order_qty,
                    price_2plus: data.is_wholesale ? null : data.price_2plus,
                    store_excludes: data.is_wholesale ? null : data.store_excludes,
                    retail_special: data.is_wholesale ? 0 : data.retail_special,
                })).post(this.getFormRoute());
            }
        },

        getFormRoute() {
            let returnUrl = route().params.return;
            if (!this.closePage) {
                returnUrl = route('products.edit', {
                    product: this.product.id,
                    return: returnUrl,
                });
            } 

            return this.route('products.update.post', {
                product: this.product.id,
                return: returnUrl,
            })
        },

        deleteProduct() {
            this.$inertia.delete(route('products.destroy', this.product.id), null, {
                onError: (errors) => {
                    console.log(errors);
                }
            });
        },
    },
}
</script>

<style scoped></style>
